import {
  basicSegmentIdentifyUser,
  basicSegmentTrackEvent,
  type ExtraEventOptions,
  type SegmentEventOptions,
  type SegmentUserData,
} from '@iconik-io/shared-lib/src/analytics/segment/segment_tracking';
import { getEntityById } from '@iconik-io/shared-lib/src/redux/entities/utils';
import type { Role } from '@iconik-io/shared-lib/src/utils/roles';

import { selectVisibleSearchFields } from '../../components/search_items/selectors';
import {
  SEARCH_CONTEXT_GLOBAL,
  SEARCH_CONTEXT_SCOPE,
  type SearchContextType,
} from '../../components/topbar_search_input/context_popover/context_popover';
import type { FEATURE_TOGGLE_OPTIONS } from '../../constants/feature_toggles';
import type { SearchScope } from '../../router/search_scope';
import type { AppDispatch, GetState } from '../../store/types';
import {
  type SearchContextEntity,
  type SearchContextPortal,
  selectFiltersCount,
} from '../search/selectors';

export const segmentTrackEvent =
  (options: SegmentEventOptions, extraOpts?: ExtraEventOptions) =>
  (_dispatch: AppDispatch, getState: GetState) => {
    basicSegmentTrackEvent({
      options,
      extraOpts,
      userData: getState().authReducer.userData,
    });
  };

type BetaProgram = (typeof FEATURE_TOGGLE_OPTIONS)[number]['value'];

type BetaType = 'Private' | 'Public';

export const segmentTrackBetaToggleEvent =
  (betaProgram: BetaProgram, betaType: BetaType, status: boolean) =>
  (dispatch: AppDispatch) => {
    dispatch(
      segmentTrackEvent({
        eventName: 'Beta Toggle',
        properties: {
          'Beta Program': betaProgram,
          'Beta Type': betaType,
          Status: status,
        },
      }),
    );
  };

export const segmentTrackOptedOutEvent =
  (betaProgram: BetaProgram, betaType: BetaType) => (dispatch: AppDispatch) => {
    dispatch(
      segmentTrackEvent({
        eventName: 'Opted out',
        properties: {
          'Beta Program': betaProgram,
          'Beta Type': betaType,
        },
      }),
    );
  };

export const segmentTrackPortalsFeatureChanged =
  (isEnabled: boolean) => (dispatch: AppDispatch) => {
    dispatch(
      segmentTrackEvent({
        eventName: 'Portals Feature Changed',
        properties: {
          Status: isEnabled ? 'enabled' : 'disabled',
          Place: 'System settings',
        },
      }),
    );
  };

const PORTAL_ROLES: Role[] = [
  'can_read_portals',
  'can_create_portals',
  'can_write_portals',
  'can_delete_portals',
];

function portalRolesFrom(roles: readonly string[] = []): Role[] {
  return PORTAL_ROLES.filter((role) => roles.includes(role));
}

export const segmentTrackRoleGroupCreated =
  (roles: readonly string[] = []) =>
  (dispatch: AppDispatch) => {
    const portalRoles = portalRolesFrom(roles);
    dispatch(
      segmentTrackEvent({
        eventName: 'Created Role Group',
        properties: {
          'Role Count': roles.length,
          'Has Portal Roles': portalRoles.length > 0,
          'Portal Roles': portalRoles,
        },
      }),
    );
  };

export const segmentTrackRoleGroupUpdated =
  ({
    previousRoles = [],
    roles = [],
    rolesChanged,
  }: {
    previousRoles?: readonly string[];
    roles?: readonly string[];
    rolesChanged: boolean;
  }) =>
  (dispatch: AppDispatch) => {
    if (!rolesChanged) {
      dispatch(
        segmentTrackEvent({
          eventName: 'Updated Role Group',
          properties: { 'Roles Changed': false },
        }),
      );
      return;
    }

    const portalRoles = portalRolesFrom(roles);
    const previousPortalRoles = portalRolesFrom(previousRoles);

    dispatch(
      segmentTrackEvent({
        eventName: 'Updated Role Group',
        properties: {
          'Roles Changed': true,
          'Role Count': roles.length,
          'Has Portal Roles': portalRoles.length > 0,
          'Portal Roles': portalRoles,
          'Portal Roles Added': portalRoles.filter(
            (role) => !previousPortalRoles.includes(role),
          ),
          'Portal Roles Removed': previousPortalRoles.filter(
            (role) => !portalRoles.includes(role),
          ),
        },
      }),
    );
  };

export const segmentTrackRoleGroupDeleted =
  (roleGroupsCount: number) => (dispatch: AppDispatch) => {
    dispatch(
      segmentTrackEvent({
        eventName: 'Deleted Role Group',
        properties: { Count: roleGroupsCount },
      }),
    );
  };

export const segmentIdentifyUser =
  (
    userId: string | undefined,
    segmentUserData: SegmentUserData,
    extraOpts: ExtraEventOptions | undefined,
  ) =>
  (_dispatch: AppDispatch, getState: GetState) => {
    const userData = userId ? getState().authReducer.userData : undefined;
    basicSegmentIdentifyUser({
      userData,
      segmentUserData,
      settings: getState().settings.merged,
      extraOpts,
    });
  };

export const pendoInitialize =
  () => (_dispatch: AppDispatch, getState: GetState) => {
    if (!pendo) {
      return;
    }

    const state = getState();

    const { userData } = state.authReducer;

    if (!userData || userData.is_super_admin) {
      return;
    }

    const userId = userData.id;
    const domainId = userData.system_domain_id;

    if (!userId || !domainId) {
      return;
    }

    const domain = getEntityById(state, 'system_domains_basic', domainId);
    pendo.initialize({
      visitor: {
        id: userId,
        type: userData.type,
        admin: userData.is_admin,
      },
      account: {
        id: domainId,
        name: domain?.name,
        type: domain?.type,
      },
    });
  };

export type SearchSentBy =
  | 'Typing'
  | 'Enter key'
  | 'Context selection'
  | 'Search button';

export type PerformSearchContext =
  | 'Global'
  | 'Collection'
  | 'Saved Search'
  | 'Favorites'
  /** The portals listing, filtered by title. */
  | 'Portals'
  /** Within the one portal the route is showing. */
  | 'Portal'
  | null;

export const SEARCH_CONTEXT_FAVORITES = 'FAVORITES';

const scopeKindLabels: Record<SearchScope['kind'], PerformSearchContext> = {
  portals: 'Portals',
};

export function resolveSearchContextLabel({
  context,
  contextEntity,
  contextPortal,
  scope,
}: {
  context: SearchContextType | typeof SEARCH_CONTEXT_FAVORITES;
  contextEntity?: SearchContextEntity;
  contextPortal?: SearchContextPortal;
  scope?: SearchScope;
}): PerformSearchContext {
  if (context === SEARCH_CONTEXT_GLOBAL) {
    return 'Global';
  }
  if (context === SEARCH_CONTEXT_FAVORITES) {
    return 'Favorites';
  }
  if (context === SEARCH_CONTEXT_SCOPE) {
    return scope ? scopeKindLabels[scope.kind] : null;
  }
  if (contextPortal) {
    return 'Portal';
  }
  if (contextEntity) {
    return 'object_type' in contextEntity ? 'Collection' : 'Saved Search';
  }
  return null;
}

export type PerformSearchEventParams = {
  query: string;
  searchContext: PerformSearchContext;
  searchSentBy: SearchSentBy;
};

export const trackPerformSearchEvent =
  ({ query, searchContext, searchSentBy }: PerformSearchEventParams) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const filtersUsed = selectFiltersCount(state);
    const sortLength = state.pages.search.list.sort.length;
    const visibleSearchFields = selectVisibleSearchFields(state).length;
    const resultsTotalCount = state.pages.search.list.pagination.total;

    dispatch(
      segmentTrackEvent({
        eventName: 'Perform Search',
        properties: {
          'Query Length': query.length,
          'Filters Used': filtersUsed,
          'Display Type': localStorage.getItem('iconikDisplay'),
          Columns:
            localStorage.getItem('iconikDisplay') === 'list'
              ? visibleSearchFields
              : 0,
          'Sorting Length': sortLength,
          'Number of Results': resultsTotalCount,
          'Sent by': searchSentBy,
          'Search Context': searchContext,
        },
      }),
    );
  };
